import { useMemo } from 'react'

import type { VipCenterData } from '../types/vip-center'
import useVipCheckPopup from './queries/useVipCheckPopup'
import useVipStatus from './queries/useVipStatus'

const useVipCenter = ({
  enablePopup = true,
  // Forwarded to useVipStatus: the VIP center page passes 'always' so opening
  // it (incl. via the sidebar "VIP access" button) re-fetches vip_status rather
  // than serving the sidebar-preloaded cache.
  refetchOnMount,
}: {
  enablePopup?: boolean
  refetchOnMount?: boolean | 'always'
} = {}) => {
  // tiers + current tier + exp all come from vip_status (all_levels holds the
  // full ladder; vip_lists is no longer called). FE-04 popup ← vip_check_popup.
  // No mock data — the page shows a loading state until status has resolved.
  const {
    data: status,
    isLoading,
    isFetching,
    isError,
    refetch: refetchStatus,
  } = useVipStatus({ refetchOnMount })
  // Only consumers that show the FE-04 popup (the VIP center page) need the
  // popup query; progress-only consumers pass enablePopup:false so they don't
  // fire vip_check_popup.
  const { data: tierUpEvent } = useVipCheckPopup(enablePopup)

  const data: VipCenterData | null = useMemo(() => {
    if (!status || !status.tiers.length) return null
    return {
      currentTierKey: status.currentTierKey ?? status.tiers[0].key,
      rechargeExp: status.rechargeExp,
      wagerExp: status.wagerExp,
      // Tier-up bonus amount is already baked into each tier from all_levels.
      tiers: status.tiers,
    }
  }, [status])

  const derived = useMemo(() => {
    if (!data) {
      return { tiers: [], currentTier: null, nextTier: null, isMaxTier: false }
    }

    const sorted = [...data.tiers].sort((a, b) => a.order - b.order)
    const currentTier =
      sorted.find(tier => tier.key === data.currentTierKey) ?? sorted[0]
    // Next tier = the first tier ranked above the current one. Don't assume
    // order is contiguous (order + 1) — a disabled/removed tier leaves a gap,
    // which would otherwise read as "max tier" and hide the real next tier.
    const nextTier =
      sorted.find(tier => tier.order > currentTier.order) ?? null

    return { tiers: sorted, currentTier, nextTier, isMaxTier: nextTier === null }
  }, [data])

  return {
    data,
    isLoading,
    // True on ANY in-flight fetch, including a background refetch that
    // already has stale cached data to serve — unlike isLoading, which is
    // false the moment cached data exists, even mid-refetch. A consumer
    // gating a restricted action on a confirmed status value (e.g.
    // hasDeposited) must check this instead of isLoading, or a stale cached
    // value keeps reading as "confirmed" while a fresher one is in flight
    // (e.g. refetchOnMount:'always' after the very deposit that flips it).
    isFetching,
    // vip_status throws on a failed fetch (see useVipStatus), so a backend
    // failure leaves `status` permanently undefined rather than resolving to
    // a fallback — every status-derived field below defaults as if the query
    // were still pending. Consumers that gate a restricted action on a
    // confirmed status value (e.g. hasDeposited) must check this too, or a
    // stuck error reads identically to "confirmed false" forever.
    isError,
    // vip_status refetch — the VIP center page calls this when the user returns
    // to the card from a subview (the page stays mounted, so refetchOnMount
    // doesn't cover that transition).
    refetchStatus,
    tierUpEvent: tierUpEvent ?? null,
    // vip_status's `upgrade` flag — gates the FE-04 popup.
    // Progress percentages come from vip_status (useVipStatus).
    rechargePct: status?.rechargePct ?? 0,
    wagerPct: status?.wagerPct ?? 0,
    showUpgradeModal: status?.showUpgrade ?? false,
    // Base-tier first-recharge bonus, surfaced through the aggregator so
    // consumers don't have to reach into useVipStatus directly.
    firstRechargeBonus: status?.firstRechargeBonus ?? 0,
    ftdClaimed: status?.ftdClaimed ?? false,
    ftdExpiresAt: status?.ftdExpiresAt ?? null,
    ftdClaimStatus: status?.ftdClaimStatus ?? null,
    isKycVerified: status?.isKycVerified ?? false,
    // FTD eligibility + first-deposit flags — drive the base-tier recharge CTA
    // (enabled vs disabled) and the redeem gate.
    ftdEligible: status?.ftdEligible ?? false,
    hasDeposited: status?.hasDeposited ?? false,
    // VIP customer-support links (from vip_status).
    supportUrl: status?.supportUrl ?? null,
    whatsapp: status?.whatsapp ?? null,
    ...derived,
  }
}

export default useVipCenter
